import { WizardPage } from '../../components/wizard/types';
import { DeclarantRndEntityRelationshipEnumDesc, DeclareAndSubmitApplicationModel } from '../../api/models';
import { FieldModel } from '../../components/form/pageForm/types';
import { titleSelectInputProps, yesNoRadioButtonProps } from '../CommonComponentProps';

const afiAppGuidanceText: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'globalContent',
    label: '',
    name: 'afiAppGuidanceText',
    contentKey: ['AFIAppGuidanceText'],
};

// rndDeclarationDeclaration is just a container for Content
const declarationTextLabel: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'declarationText',
    label: '',
    name: 'rndDeclarationDeclaration',
    contentKey: ['RndDeclaration'],
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: true,
    },
};

const declarantDetailsLabel: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'labelSubHeading',
    label: 'Declarant details',
    name: 'declarantDetailsLabel',
    contentKey: ['RnDDeclarantDetails'],
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: true,
    },
};

const primaryCompanyContact: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'declarantInput',
    label: '',
    name: 'primaryCompanyContact',
};

const title = titleSelectInputProps('title', 'Title (optional)', true);

const titleOther: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Other title',
    name: 'titleOther',
    maxWidth: '10rem',
    maxLength: 30,
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const firstName: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'First name',
    name: 'firstName',
    maxWidth: '22rem',
    maxLength: 50,
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const lastName: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Last name',
    name: 'lastName',
    maxWidth: '22rem',
    maxLength: 50,
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const position: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Position or role',
    name: 'position',
    maxWidth: '22rem',
    maxLength: 50,
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const phone: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Phone number',
    name: 'phone',
    maxWidth: '16.25rem',
    contentKey: ['RnDDeclarantPhoneNumber'],
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const email: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Email',
    name: 'email',
    maxWidth: '22rem',
    maxLength: 100,
    contentKey: ['ContactDetailsEmail'],
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const entityDetails: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'abnAcnLookup',
    label: 'Company ABN',
    name: 'entityDetails',
    contentKey: ['RnDDeclarantCompanyAbn'],
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: false,
    },
};

const potentialIneligibilityRisksHeading: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'labelSubHeading',
    label: 'Potential risks',
    name: 'potentialIneligibilityRisks',
    componentProps: {
        linkField: [
            'asicWarningAcknowledged',
            'anzsicWarningAcknowledged',
            'subsidiaryWarningAcknowledged',
            'incomePeriodWarningAcknowledged',
        ],
    },
};

const potentialIneligibilityRisksDescription: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'content',
    name: 'potentialIneligibilityRisksDescription',
    label: '',
    contentKey: ['rndEligibilityReview'],
    componentProps: {
        linkField: [
            'asicWarningAcknowledged',
            'anzsicWarningAcknowledged',
            'subsidiaryWarningAcknowledged',
            'incomePeriodWarningAcknowledged',
        ],
    },
};

const potentialIneligibilityRisksSubHeading: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'heading',
    label: 'I acknowledge I am aware of the potential risks',
    name: 'potentialIneligibilityRisksSubHeading',
    componentProps: {
        tag: 'h3',
        className: 'rdtiH2',
        linkField: [
            'asicWarningAcknowledged',
            'anzsicWarningAcknowledged',
            'subsidiaryWarningAcknowledged',
            'incomePeriodWarningAcknowledged',
        ],
    },
};

const asicWarningAcknowledged: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'checkbox',
    heading: '**The ABN / ACN provided for the organisation is not registered with the Australian Securities & Investment Commission (ASIC)**',
    label: 'I acknowledge that the application is for a company that is not registered with the Australian Securities & Investments Commission (ASIC). I understand that in order to apply for the R&D Tax Incentive the company must be registered with ASIC.',
    name: 'asicWarningAcknowledged',
};

const anzsicWarningAcknowledged: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'checkbox',
    heading: `**There are Tax Payer alerts and / or specific guidance relevant to your company's primary industry of operation.  Please confirm that you have considered the following guidance to ensure you have correctly assessed your claim as eligible.**`,
    label: `I acknowledge that I have reviewed and understood the Tax Payer alerts and / or BGA guidance that are relevant to my company's primary industry of operation.`,
    name: 'anzsicWarningAcknowledged',
};

const subsidiaryWarningAcknowledged: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'checkbox',
    heading: '**The ABN provided for the organisation is for a subsidiary of a consolidated or multiple entry consolidated (MEC) group**',
    label: 'I acknowledge that my application is for a subsidiary of a consolidated or multiple entry consolidated (MEC) group. I understand that only the head entity of a consolidated or MEC group is eligible for the R&D Tax Incentive. I understand that this may affect my eligibility for the R&D Tax Incentive.',
    name: 'subsidiaryWarningAcknowledged',
    contentKey: ['SubsidiaryMecGroupAdviceReview'],
};

const incomePeriodWarningAcknowledged: FieldModel<DeclareAndSubmitApplicationModel> = {
    /* tslint:disable */
    component: 'checkbox',
    heading: '**The stated income period does not match ATO records**',
    label: 'I acknowledge that the stated income period does not match ATO records. I understand that this may affect my eligibility for the R&D Tax Incentive.',
    name: 'incomePeriodWarningAcknowledged',
    contentKey: ['ATOIncomePeriodAdviceReview'],
};

const isDeclarantSameAsPrimaryCompanyContact: FieldModel<DeclareAndSubmitApplicationModel> = yesNoRadioButtonProps(
    'isDeclarantSameAsPrimaryCompanyContact',
    'Is the Declarant the same as the Primary Company contact?',
    ['AFIDeclarationSameAsPrimaryCompanyContact'],
    {
        displayFlag: 'twoLines',
        displayWhenEmpty: true,
    },
);

const acknowledgedByHeading: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'labelSubHeading',
    label: 'Acknowledged by:',
    name: '',
    componentProps: {
        tag: 'h3',
        linkField: [
            'asicWarningAcknowledged',
            'anzsicWarningAcknowledged',
            'subsidiaryWarningAcknowledged',
            'incomePeriodWarningAcknowledged',
        ],
    },
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: true,
    },
};

const acknowledgerName: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Name:',
    name: 'acknowledgerName',
    pdfFieldProps: {
        displayFlag: 'singleLine',
        displayWhenEmpty: true,
    },
};

const acknowledgerEmployerAbn: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'textInput',
    label: 'Employer ABN:',
    name: 'acknowledgerEmployerAbn',
    pdfFieldProps: {
        displayFlag: 'singleLine',
        displayWhenEmpty: false,
    },
};

const hasAcceptedDeclaration: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'checkbox',
    legend: '',
    label: 'I agree',
    name: 'hasAcceptedDeclaration',
    maxWidth: '22rem',
};

const declarantRelationshipWithCompany: FieldModel<DeclareAndSubmitApplicationModel> = {
    component: 'radiobuttonGroup',
    label: 'What is the Declarant\'s relationship with the R&D entity?',
    name: 'declarantRelationshipWithCompany',
    componentProps: {
        name: 'declarantRelationshipWithCompany',
        radioOptions: [
            {
                label: DeclarantRndEntityRelationshipEnumDesc['AuthorizedPerson'],
                value: 'AuthorizedPerson',
            },
            {
                label: DeclarantRndEntityRelationshipEnumDesc['ActingOnBehalfPerson'],
                value: 'ActingOnBehalfPerson',
            },
        ],
    },
    pdfFieldProps: {
        displayFlag: 'twoLines',
        displayWhenEmpty: true,
    },
};

const pdfDeclarationAndSubmission: WizardPage<DeclareAndSubmitApplicationModel> = {
    enablingPages: [],
    apiEndpoint: '',
    name: 'pdfDeclarationAndSubmission',
    title: 'Declare and submit',
    fields: {
        afiAppGuidanceText,
        isDeclarantSameAsPrimaryCompanyContact,
        declarantDetailsLabel,
        primaryCompanyContact,
        title,
        titleOther,
        firstName,
        lastName,
        position,
        phone,
        email,
        entityDetails,
        declarantRelationshipWithCompany,
        declarationTextLabel,
        hasAcceptedDeclaration,
        potentialIneligibilityRisksHeading,
        potentialIneligibilityRisksDescription,
        potentialIneligibilityRisksSubHeading,
        asicWarningAcknowledged,
        anzsicWarningAcknowledged,
        subsidiaryWarningAcknowledged,
        incomePeriodWarningAcknowledged,
        acknowledgedByHeading,
        acknowledgerName,
        acknowledgerEmployerAbn,
    },
    initialPageValues: {
        isDeclarantSameAsPrimaryCompanyContact: undefined,
        primaryCompanyContact: {
            title: undefined,
            titleOther: undefined,
            firstName: undefined,
            lastName: undefined,
            position: undefined,
            phone: undefined,
            email: undefined,
        },
        email: undefined,
        entityDetails: undefined,
        firstName: undefined,
        lastName: undefined,
        phone: undefined,
        position: undefined,
        title: undefined,
        titleOther: undefined,
        asicWarningAcknowledged: undefined,
        anzsicWarningAcknowledged: undefined,
        subsidiaryWarningAcknowledged: undefined,
        incomePeriodWarningAcknowledged: undefined,
        acknowledgerName: undefined,
        acknowledgerEmployerAbn: undefined,
        declarantRelationshipWithCompany: undefined,
        hasAcceptedDeclaration: undefined,
    },
    pdfProps: {
        isVisible: true,
    },
};

export default pdfDeclarationAndSubmission;
